import http from '@/plugins/http';
import { handleError } from './errors';
import {
  projectUserListResponseSchema,
  workspaceMemberOrGroupListResponseSchema,
  type PatchProjectMemberRequest,
  type ProjectMemberListResponse,
  type WorkspaceMemberOrGroupListResponse,
  type WorkspaceMemberParams,
} from '@/types/collaboration';
import ExpiryMap from 'expiry-map';
import { memoize } from './cache';

// Endpoint to get members (users and groups) in workspace: /api/workspaces/{workspace_id}/userlist
export async function getWorkspaceUsersAndGroupsList(
  workspaceUUID: string,
  queryParam?: WorkspaceMemberParams
): Promise<WorkspaceMemberOrGroupListResponse> {
  return handleError(async () => {
    const params: Record<string, string | number> = {};

    if (queryParam?.page !== undefined && queryParam.page !== null) {
      params.page = queryParam.page;
    }

    if (queryParam?.size !== undefined && queryParam.size !== null) {
      params.size = queryParam.size;
    }

    if (queryParam?.email_filter !== undefined && queryParam.email_filter !== null) {
      params.email_filter = queryParam.email_filter;
    }

    const response = await http.get(`/api/workspaces/${workspaceUUID}/userlist`, {
      params,
    });
    const parsedData = workspaceMemberOrGroupListResponseSchema.parse(response.data);

    // Normalize data: ensure all users have a type field
    // Backend may not send type field when feature flag is disabled
    const normalizedItems = parsedData.items.map((item) => {
      if ('type' in item) {
        return item; // Already has type field (user with type or group)
      }
      // User without type field - add it
      return { ...item, type: 'user' as const };
    });

    return {
      ...parsedData,
      items: normalizedItems,
    };
  });
}

export const workspaceUsersAndGroupsListCache = new ExpiryMap(5 * 60 * 1000);
export const memoizedGetWorkspaceUsersAndGroupsList = memoize(
  getWorkspaceUsersAndGroupsList,
  workspaceUsersAndGroupsListCache
);

// Endpoint to get members in a project: /api/workspaces/{workspace_id}/projects/{project_id}/userlist
export async function getProjectUserlist(
  workspaceUUID: string,
  projectUUID: string
): Promise<ProjectMemberListResponse> {
  return handleError(async () => {
    const response = await http.get(
      `/api/workspaces/${workspaceUUID}/projects/${projectUUID}/userlist`
    );

    return projectUserListResponseSchema.parse(response.data);
  });
}
export const projectUserlistCache = new ExpiryMap(5 * 60 * 1000);
export const memoizedGetProjectUserlist = memoize(getProjectUserlist, projectUserlistCache);

// Endpoint to update project members: /api/workspaces/{workspace_id}/projects/{project_id}/userlist
export async function updateProjectUserList(
  workspaceUUID: string,
  projectUUID: string,
  body: PatchProjectMemberRequest
) {
  return handleError(
    async () =>
      await http.patch(`/api/workspaces/${workspaceUUID}/projects/${projectUUID}/userlist`, {
        body,
      })
  );
}
